/*
 * Amara PDP — progressive enhancement only.
 *
 * Commerce behaviour (add to cart, variant selection, cart drawer, wallet
 * buttons) stays owned by the theme's product-form.js / variant-picker.js and
 * Shopify's native payment button. Nothing here re-implements checkout logic.
 *
 * Responsibilities:
 *   1. Gallery — scroll-snap sync, thumbnails, keyboard nav, counter, lightbox.
 *   2. Sticky add to cart — reveal once the primary CTA leaves the viewport.
 *   3. Variant change — keep gallery, sticky price and sticky variant id in sync.
 */
(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-amara-product]';
  const READY_FLAG = 'amaraProductReady';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- gallery */

  class AmaraGallery {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-gallery-track]');
      if (!this.track) return;

      this.slides = Array.from(root.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
      this.dots = Array.from(root.querySelectorAll('[data-gallery-dot]'));
      this.currentLabel = root.querySelector('[data-gallery-current]');
      this.totalLabel = root.querySelector('[data-gallery-total]');
      this.index = 0;

      // Two vocabularies, kept apart on purpose:
      //   index    = a slide's original position (data-index), stable forever
      //   position = its place among the slides currently shown
      // Thumbs, dots and zoom data are addressed by index; scrolling and
      // counters work in positions, so hidden slides never count.
      this.visible = this.slides.map((_, index) => index);

      this.readVariantMap();

      this.bindTrack();
      this.bindThumbs();
      this.bindKeyboard();
      this.bindLightbox();

      if (this.variantMap) this.applyVariant(root.dataset.initialVariantId, false);
    }

    /* ----------------------------------------------------- variant galleries */

    readVariantMap() {
      const node = this.root.querySelector('[data-variant-gallery-map]');
      if (!node) return;

      try {
        const raw = JSON.parse(node.textContent);
        this.variantMap = {};
        Object.keys(raw).forEach((variantId) => {
          const ids = Array.isArray(raw[variantId]) ? raw[variantId] : String(raw[variantId] || '').split('|');
          this.variantMap[String(variantId)] = ids.map((id) => String(id).trim()).filter(Boolean);
        });
      } catch (error) {
        console.warn('Amara PDP: could not parse the variant gallery map.', error);
      }
    }

    positionOf(index) {
      return this.visible.indexOf(index);
    }

    isVisible(index) {
      return this.visible.includes(index);
    }

    /* Shows only the media listed for a variant. A variant with no list, or a
       list that matches none of this product's media, shows everything. */
    applyVariant(variantId, scroll = true) {
      if (!this.variantMap) return;

      const ids = this.variantMap[String(variantId)] || [];
      let allowed = new Set(ids);
      let next = this.slides
        .map((slide, index) => (allowed.has(slide.dataset.mediaId) ? index : -1))
        .filter((index) => index >= 0);

      if (!next.length) next = this.slides.map((_, index) => index);

      const changed = next.join(',') !== this.visible.join(',');
      this.visible = next;

      this.slides.forEach((slide, index) => {
        const shown = this.isVisible(index);
        slide.hidden = !shown;
        slide.setAttribute('aria-hidden', String(!shown));
      });
      this.thumbs.forEach((thumb, index) => {
        thumb.hidden = !this.isVisible(index);
      });
      this.dots.forEach((dot, index) => {
        dot.hidden = !this.isVisible(index);
      });

      if (this.totalLabel) this.totalLabel.textContent = String(this.visible.length);
      // One image on show: the desktop grid gives it the full width.
      this.root.classList.toggle('amara-gallery--single', this.visible.length === 1);
      this.paintLightboxTotal();

      if (!changed) return;

      // Land on the first slide of the new set; the variant's own featured
      // image, if any, is scrolled to by the caller right after this.
      this.setActive(this.visible[0], false);
      if (scroll) this.scrollToPosition(0, true);
    }

    /* ----------------------------------------------------------------- track */

    bindTrack() {
      if (this.slides.length < 2) return;

      // IntersectionObserver on the scroll container beats a scroll listener:
      // it survives momentum scrolling on iOS without rAF throttling.
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.55) {
              this.setActive(Number(entry.target.dataset.index), false);
            }
          });
        },
        { root: this.track, threshold: [0.55] }
      );

      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    bindThumbs() {
      const go = (event) => {
        const button = event.target.closest('[data-gallery-thumb]');
        if (!button) return;
        this.scrollToPosition(this.positionOf(Number(button.dataset.index)));
      };

      const thumbsWrap = this.root.querySelector('[data-gallery-thumbs]');
      if (thumbsWrap) thumbsWrap.addEventListener('click', go);
    }

    bindKeyboard() {
      if (this.slides.length < 2) return;

      this.track.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        this.scrollToPosition(Math.max(0, this.positionOf(this.index)) + delta);
      });
    }

    setActive(index, scroll = true) {
      if (!this.isVisible(index)) return;

      this.index = index;

      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      if (this.currentLabel) this.currentLabel.textContent = String(this.positionOf(index) + 1);
      if (scroll) this.scrollToPosition(this.positionOf(index));
    }

    /* Scrolls the track to the slide at a position among the visible slides,
       wrapping at both ends. */
    scrollToPosition(position, instant = false) {
      const count = this.visible.length;
      if (!count) return;

      const slide = this.slides[this.visible[((position % count) + count) % count]];
      if (!slide) return;

      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }

    /* Shows the media belonging to a variant, when that media is on show. */
    showMediaId(mediaId) {
      if (!mediaId) return;
      const slide = this.slides.find((item) => item.dataset.mediaId === String(mediaId));
      if (!slide) return;

      const index = Number(slide.dataset.index);
      if (!this.isVisible(index)) return;

      this.setActive(index, false);
      this.scrollToPosition(this.positionOf(index));
    }

    /* ------------------------------------------------------------ lightbox */

    bindLightbox() {
      this.dialog = this.root.querySelector('[data-gallery-lightbox]');
      const trigger = this.root.querySelector('[data-gallery-zoom]');
      const dataNode = this.root.querySelector('[data-gallery-zoom-data]');

      if (!this.dialog || !dataNode || typeof this.dialog.showModal !== 'function') {
        if (trigger) trigger.hidden = true;
        return;
      }

      try {
        this.zoomImages = JSON.parse(dataNode.textContent);
      } catch (error) {
        console.warn('Amara PDP: could not parse zoom data.', error);
        if (trigger) trigger.hidden = true;
        return;
      }

      this.lightboxImage = this.dialog.querySelector('[data-lightbox-image]');
      this.lightboxCounter = this.dialog.querySelector('[data-lightbox-current]');
      this.lightboxTotal = this.dialog.querySelector('[data-lightbox-total]');
      this.paintLightboxTotal();

      if (trigger) trigger.addEventListener('click', () => this.openLightbox(this.index));

      this.track.addEventListener('click', (event) => {
        // Never hijack a click on a video control or model viewer.
        if (event.target.closest('video, model-viewer, iframe')) return;

        // On the desktop grid every slide is on screen at once, so open the
        // image that was actually clicked rather than the carousel's index.
        const slide = event.target.closest('[data-gallery-slide]');
        this.openLightbox(slide ? Number(slide.dataset.index) : this.index);
      });

      this.dialog
        .querySelector('[data-lightbox-close]')
        ?.addEventListener('click', () => this.dialog.close());

      this.dialog
        .querySelector('[data-lightbox-prev]')
        ?.addEventListener('click', () => this.stepLightbox(-1));

      this.dialog
        .querySelector('[data-lightbox-next]')
        ?.addEventListener('click', () => this.stepLightbox(1));

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.stepLightbox(1);
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
      });

      // Click the backdrop (i.e. the dialog element itself) to dismiss.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('close', () => {
        this.setActive(this.lightboxIndex ?? this.index);
      });
    }

    /* Visible slides that have a zoomable image (videos and models do not). */
    zoomable() {
      return this.visible.filter((index) => this.zoomImages?.[index]);
    }

    paintLightboxTotal() {
      if (this.lightboxTotal) this.lightboxTotal.textContent = String(this.zoomable().length);
    }

    openLightbox(index) {
      if (!this.paintLightbox(index)) return;
      this.dialog.showModal();
    }

    stepLightbox(delta) {
      const images = this.zoomable();
      if (!images.length) return;

      const position = Math.max(0, images.indexOf(this.lightboxIndex ?? this.index));
      this.paintLightbox(images[(position + delta + images.length) % images.length]);
    }

    paintLightbox(index) {
      const images = this.zoomable();
      const entry = images.includes(index) ? this.zoomImages[index] : null;
      if (!entry) return false;

      this.lightboxIndex = index;
      this.lightboxImage.src = entry.src;
      this.lightboxImage.alt = entry.alt || '';
      if (this.lightboxCounter) this.lightboxCounter.textContent = String(images.indexOf(index) + 1);
      return true;
    }
  }

  /* ------------------------------------------------------------ sticky ATC */

  function initStickyAtc(section) {
    const sticky = section.querySelector('[data-amara-sticky]');
    const anchor = section.querySelector('[data-amara-atc-anchor]');
    if (!sticky || !anchor) return null;

    sticky.hidden = false;

    const setVisible = (visible) => {
      sticky.classList.toggle('is-visible', visible);
      document.body.classList.toggle('amara-sticky-visible', visible);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only reveal once the CTA has scrolled off the TOP of the viewport,
        // so the bar never covers a CTA the customer has not reached yet.
        const scrolledPast = entry.boundingClientRect.top < 0;
        setVisible(!entry.isIntersecting && scrolledPast);
      },
      { threshold: 0 }
    );

    observer.observe(anchor);
    return sticky;
  }

  /* ------------------------------------------------------------ size guide */

  // Markup: snippets/amara-size-guide.liquid. The dialog is native, so Escape
  // and focus trapping come from the browser; this adds tabs, the unit switch,
  // scroll locking, backdrop dismissal and focus return.
  function initSizeGuide(section) {
    const root = section.querySelector('[data-amara-size-guide]');
    if (!root || root.dataset.sizeGuideReady === 'true') return;

    const trigger = root.querySelector('[data-size-guide-open]');
    const dialog = root.querySelector('[data-size-guide-dialog]');
    if (!trigger || !dialog || typeof dialog.showModal !== 'function') {
      if (trigger) trigger.hidden = true;
      return;
    }
    root.dataset.sizeGuideReady = 'true';

    const tabs = Array.from(dialog.querySelectorAll('[data-size-guide-tab]'));
    const panels = Array.from(dialog.querySelectorAll('[data-size-guide-panel]'));
    const units = Array.from(dialog.querySelectorAll('[data-size-guide-unit]'));
    const cells = Array.from(dialog.querySelectorAll('[data-mm]'));

    /* ----- tabs */

    const selectTab = (name, focus = false) => {
      tabs.forEach((tab) => {
        const active = tab.dataset.sizeGuideTab === name;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && focus) tab.focus();
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.sizeGuidePanel !== name;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => selectTab(tab.dataset.sizeGuideTab));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        selectTab(tabs[next].dataset.sizeGuideTab, true);
      });
    });

    /* ----- units: every measurement is stored in millimetres on the cell.
       Bracelets read best in cm, rings in mm, so the metric unit follows
       the size of the number. Ranges ("140-150") convert both ends. */

    const format = (mm, imperial) => {
      if (imperial) return `${(mm / 25.4).toFixed(2)}`;
      return mm >= 100 ? `${(mm / 10).toFixed(1)}` : `${mm.toFixed(1)}`;
    };

    const unitLabel = (mm, imperial) => (imperial ? 'in' : mm >= 100 ? 'cm' : 'mm');

    const paintUnits = (system) => {
      const imperial = system === 'imperial';
      cells.forEach((cell) => {
        const parts = String(cell.dataset.mm).split('-').map(Number);
        if (parts.some((part) => Number.isNaN(part))) return;
        const label = unitLabel(parts[0], imperial);
        cell.textContent = `${parts.map((part) => format(part, imperial)).join(' – ')} ${label}`;
      });
      units.forEach((button) => {
        const active = button.dataset.sizeGuideUnit === system;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    units.forEach((button) => {
      button.addEventListener('click', () => paintUnits(button.dataset.sizeGuideUnit));
    });
    paintUnits('metric');

    /* ----- open / close */

    const open = () => {
      selectTab(root.dataset.defaultTab || 'bracelets');
      dialog.showModal();
      document.documentElement.classList.add('amara-modal-open');
      dialog.querySelector('[data-size-guide-close]')?.focus();
    };

    trigger.addEventListener('click', open);

    dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', () => dialog.close());

    // A click on the backdrop lands on the dialog element itself.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('amara-modal-open');
      trigger.focus();
    });
  }

  /* ------------------------------------------------------- smart sticky */

  // Two-column PDP: whichever column is shorter than the other stays in view.
  //
  // CSS already makes both columns `position: sticky; top: <offset>`, which is
  // right whenever a column fits in the viewport. A column that is taller than
  // the viewport but shorter than its neighbour needs more: pinned at the top
  // its tail is unreachable until the neighbour ends. So for that case this
  // switches between three states as the customer scrolls:
  //   top     sticky, top edge pinned (while scrolling up)
  //   bottom  sticky, bottom edge pinned (while scrolling down)
  //   free    relative, offset to where it was when the direction changed, so
  //           it scrolls with the page until the opposite edge is reached
  // Every hand-over happens at the column's current position, so nothing jumps.
  function initSmartSticky(section) {
    const primary = section.querySelector('.amara-pdp__primary');
    if (!primary || primary.dataset.awStickyReady === 'true') return;
    primary.dataset.awStickyReady = 'true';

    const columns = Array.from(primary.querySelectorAll('.amara-pdp__gallery-col, .amara-pdp__info-col'));
    if (columns.length < 2) return;

    const desktop = window.matchMedia('(min-width: 1024px)');
    const TOP_GAP = 96; // matches the CSS offset below the sticky header
    const BOTTOM_GAP = 24;
    const states = new Map();
    let lastY = window.scrollY;
    let frame = 0;

    const clear = (column) => {
      column.style.position = '';
      column.style.top = '';
      states.set(column, 'css');
    };

    const setTopPinned = (column) => {
      column.style.position = 'sticky';
      column.style.top = `${TOP_GAP}px`;
      states.set(column, 'top');
    };

    const setBottomPinned = (column, height) => {
      column.style.position = 'sticky';
      column.style.top = `${window.innerHeight - height - BOTTOM_GAP}px`;
      states.set(column, 'bottom');
    };

    const setFree = (column) => {
      // Freeze the column where it is: its current distance from the top of
      // the row becomes a relative offset.
      const offset = column.getBoundingClientRect().top - primary.getBoundingClientRect().top;
      column.style.position = 'relative';
      column.style.top = `${Math.max(0, offset)}px`;
      states.set(column, 'free');
    };

    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const goingDown = y > lastY;
      const stationary = y === lastY;
      lastY = y;

      if (!desktop.matches) {
        columns.forEach((column) => { if (states.get(column) !== 'css') clear(column); });
        return;
      }

      const rowHeight = primary.getBoundingClientRect().height;
      const available = window.innerHeight - TOP_GAP - BOTTOM_GAP;

      columns.forEach((column) => {
        const height = column.getBoundingClientRect().height;
        const needsScript = height > available && height < rowHeight - 1;

        if (!needsScript) {
          if (states.get(column) !== 'css') clear(column);
          return;
        }

        const state = states.get(column) || 'css';
        const rect = column.getBoundingClientRect();

        if (state === 'css') {
          // Entering script control: start pinned at the top, like the CSS was.
          setTopPinned(column);
          return;
        }

        if (stationary) return;

        if (goingDown) {
          if (state === 'top') setFree(column);
          else if (state === 'free' && rect.bottom <= window.innerHeight - BOTTOM_GAP) setBottomPinned(column, height);
        } else {
          if (state === 'bottom') setFree(column);
          else if (state === 'free' && rect.top >= TOP_GAP) setTopPinned(column);
        }
      });
    };

    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    desktop.addEventListener('change', schedule);

    // Accordions, variant changes and images loading all change heights.
    const observer = new ResizeObserver(schedule);
    columns.forEach((column) => observer.observe(column));
    observer.observe(primary);

    update();
  }

  /* ----------------------------------------------------------- description */

  function initDetails(section) {
    // Nothing to wire up: <details> is native. Kept as a hook point so the
    // markup contract stays visible from the JS side.
    return section.querySelectorAll('[data-amara-details]').length;
  }

  /* ------------------------------------------------------------------ init */

  function init(section) {
    if (!section || section.dataset[READY_FLAG] === 'true') return;
    section.dataset[READY_FLAG] = 'true';

    const galleryRoot = section.querySelector('[data-amara-gallery]');
    const gallery = galleryRoot ? new AmaraGallery(galleryRoot) : null;
    const sticky = initStickyAtc(section);

    initDetails(section);
    initSizeGuide(section);
    initSmartSticky(section);

    const productId = section.dataset.productId;
    if (!productId || !window.MinimogEvents?.subscribe) return;

    window.MinimogEvents.subscribe(`${productId}__VARIANT_CHANGE`, (variant) => {
      if (!variant) return;

      section.classList.toggle('is-unavailable', !variant.available);

      // Narrow the gallery to this variant's media first, then land on its
      // featured image when that image is part of the set.
      if (gallery) {
        gallery.applyVariant(variant.id);
        if (variant.featured_media?.id) gallery.showMediaId(variant.featured_media.id);
      }

      if (!sticky) return;

      // variant-picker.js only rewrites #product-form-<section> and
      // #product-form-installment, so the sticky form is ours to keep current.
      const stickyId = sticky.querySelector('[name="id"]');
      if (stickyId) stickyId.value = variant.id;

      const stickyPrice = sticky.querySelector('[data-amara-sticky-price]');
      const formatMoney = window.Shopify?.formatMoney;
      if (stickyPrice && typeof formatMoney === 'function') {
        stickyPrice.innerHTML = formatMoney(
          variant.price,
          window.MinimogSettings?.money_format
        );
      }
    });
  }

  function scan(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) init(root);
    root.querySelectorAll?.(SECTION_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
